/**
 * The one way into a management surface: an operator types their email and
 * password and the server hands back a session cookie
 * (contracts/protected-surfaces.md §4, FR-003).
 *
 * It is `email-capture-form.tsx` with a second field and a different ending —
 * same Paper controls, same polite live region, same pending discipline — so
 * the two forms on this site behave identically to anyone using either. What
 * differs is what a success means, and that is the whole of the interesting
 * part below.
 *
 * ## Success leaves the SPA on purpose
 *
 * `window.location.assign` rather than the router, and it is load-bearing three
 * ways (research D10). Sidekiq's dashboard is server-rendered, so no amount of
 * client routing can show it. The fresh document arrives carrying the CSRF meta
 * tag Rails rotated when the session was created, which is the token every
 * later mutating request reads. And it flattens the "already signed in" edge:
 * Devise answers a re-submitted sign-in with a 302, the api-client follows it,
 * the non-JSON page it lands on parses as `{}`, and a body with no `redirectTo`
 * resolves through `resolveRedirectTarget` to `/sidekiq` — a management surface
 * rather than an error, with nothing here special-casing it.
 *
 * Where to land is the server's decision (FR-005): it stored the page that was
 * asked for before the redirect. `resolveRedirectTarget` — pure, exported and
 * unit-tested in the api-store module — is what keeps that value from becoming
 * an open redirect. No client logic of consequence lives in this file.
 *
 * ## Two failures, told apart by status alone
 *
 * A 401 is the server saying the pair does not match — deliberately the same
 * answer for an unknown email as for a wrong password (FR-007), so the message
 * says the same and names neither field. Anything else — a 500, a dropped
 * connection — is not about what was typed, and telling an operator their
 * credentials were refused when the request never arrived would send them
 * hunting for a password that is fine.
 *
 * ## Announcing without moving focus
 *
 * The wrapper is the live region and it is mounted from first paint, which is
 * what makes the error inside it announce when it appears; a region that
 * arrives with its own content usually says nothing. Nothing calls `focus()`.
 * The message is additionally tied to both fields with `aria-describedby`, so
 * tabbing back to either one hears why the submit was refused — both, because
 * the rejection is of the pair and singling out a field would claim knowledge
 * the server deliberately withholds.
 *
 * ## This page's one `<h1>` (FR-019(e))
 *
 * It lives here rather than in the route, the way `not-found.tsx` carries its
 * own: the route is layout, this is the page. The header's wordmark is a brand
 * link rather than a heading, so this is the only one.
 *
 * ## Notes on the two libraries underneath
 *
 * There is no `<form>` element — react-native-web renders a `<div>` around each
 * `<input>`, so there is no implicit submit. `onSubmitEditing` on both fields
 * restores Enter, which is what a real form would have done. Paper's `label` is
 * decoration rather than a `<label for>`, so each field's accessible name is
 * given explicitly. `HelperText` is rendered only when there is something to
 * say: Paper keeps a `visible={false}` helper mounted at zero opacity, which
 * would leave the message in the accessibility tree permanently.
 */

import { useState } from "react";
import { StyleSheet, View } from "react-native";
import { Button, HelperText, Text, TextInput } from "react-native-paper";
import {
  HttpError,
  resolveRedirectTarget,
  useSignInOperator,
} from "@/lib/api-store";
import { headingProps } from "@/lib/heading-props";
import { navigateTo } from "@/lib/navigate";
import {
  CONNECTION_FAILED_MESSAGE,
  FORM_MAX_WIDTH,
  invalidFieldProps,
} from "@/components/forms/form-shared";
import { useAppTheme } from "@/theme/paper-theme";
import { spacing } from "@/theme/tokens";

const HEADLINE = "Sign in";

const EMAIL_LABEL = "Email address";
const PASSWORD_LABEL = "Password";

const SUBMIT_LABEL = "Sign in";

/**
 * The status the failure app answers a refused sign-in with
 * (contracts/operator-session-api.md §4). It is the only status this form reads;
 * every other one means the same thing to it.
 */
const UNAUTHORIZED_STATUS = 401;

/**
 * Names neither field and neither half of the pair, because the server does not
 * either (FR-007) — an unknown email and a wrong password are one answer, and a
 * message that narrowed it down would hand back the account existence the 401
 * is careful not to disclose.
 */
const CREDENTIALS_REJECTED_MESSAGE = "Email or password didn't match.";

/** One sign-in form exists on the site, so a literal id reads better than a generated one. */
const ERROR_MESSAGE_ID = "sign-in-error";

const PAGE_HEADING_PROPS = headingProps(1);

export function SignInForm() {
  const theme = useAppTheme();
  const signIn = useSignInOperator();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  /**
   * Success is held alongside pending rather than replaced by it. `assign`
   * returns immediately and the document is torn down some moments later, and
   * an operator looking at a form that went idle again in that gap would
   * reasonably press the button a second time.
   */
  const isSubmitting = signIn.isPending || signIn.isSuccess;

  const wasRejected =
    signIn.error instanceof HttpError &&
    signIn.error.status === UNAUTHORIZED_STATUS;

  const errorMessage = signIn.isError
    ? wasRejected
      ? CREDENTIALS_REJECTED_MESSAGE
      : CONNECTION_FAILED_MESSAGE
    : null;

  /**
   * Editing is the operator answering the message, so it goes quiet and the
   * form is idle again. Neither typed value is touched by any of this — a
   * refused sign-in keeps what was entered so the correction is a keystroke
   * rather than a retype.
   */
  function clearFailure() {
    if (signIn.isError) {
      signIn.reset();
    }
  }

  function handleEmailChange(value: string) {
    setEmail(value);
    clearFailure();
  }

  function handlePasswordChange(value: string) {
    setPassword(value);
    clearFailure();
  }

  /**
   * The early return is what actually stops a double-send: a disabled input
   * still receives `keydown` in the browser, so `onSubmitEditing` can fire
   * while a request is in flight.
   *
   * Nothing is validated here and nothing is normalized. An empty or malformed
   * pair is refused by the server with the message it would have shown anyway,
   * and a second normalizer in front of the authoritative one (FR-002 matches
   * email case-insensitively; a password is a literal) could only turn a
   * correct password into a failed sign-in.
   */
  function handleSubmit() {
    if (isSubmitting) {
      return;
    }

    signIn.mutate(
      { email, password },
      {
        onSuccess: (session) => {
          navigateTo(resolveRedirectTarget(session.redirectTo));
        },
      },
    );
  }

  return (
    <View accessibilityLiveRegion="polite" style={styles.region}>
      <Text
        variant="headlineMedium"
        style={{ color: theme.colors.onSurface }}
        {...PAGE_HEADING_PROPS}
      >
        {HEADLINE}
      </Text>

      <TextInput
        mode="outlined"
        label={EMAIL_LABEL}
        aria-label={EMAIL_LABEL}
        value={email}
        onChangeText={handleEmailChange}
        onSubmitEditing={handleSubmit}
        // react-native-web blurs a single-line field after Enter, which would
        // drop the caret to `<body>` on the error path — where both fields
        // survive the submit and one of them still has to be corrected.
        blurOnSubmit={false}
        inputMode="email"
        autoComplete="email"
        autoCapitalize="none"
        autoCorrect={false}
        returnKeyType="next"
        disabled={isSubmitting}
        error={errorMessage !== null}
        {...(errorMessage !== null ? invalidFieldProps(ERROR_MESSAGE_ID) : {})}
      />

      <TextInput
        mode="outlined"
        label={PASSWORD_LABEL}
        aria-label={PASSWORD_LABEL}
        value={password}
        onChangeText={handlePasswordChange}
        onSubmitEditing={handleSubmit}
        blurOnSubmit={false}
        secureTextEntry
        // The password manager's cue: `current-password` offers a saved entry
        // for this site rather than a generated one, which is `new-password`.
        autoComplete="current-password"
        autoCapitalize="none"
        autoCorrect={false}
        returnKeyType="go"
        disabled={isSubmitting}
        error={errorMessage !== null}
        {...(errorMessage !== null ? invalidFieldProps(ERROR_MESSAGE_ID) : {})}
      />

      {errorMessage !== null ? (
        <HelperText type="error" visible id={ERROR_MESSAGE_ID}>
          {errorMessage}
        </HelperText>
      ) : null}

      <Button
        mode="contained"
        onPress={handleSubmit}
        loading={isSubmitting}
        disabled={isSubmitting}
        accessibilityLabel={SUBMIT_LABEL}
      >
        {SUBMIT_LABEL}
      </Button>
    </View>
  );
}

const styles = StyleSheet.create({
  region: {
    maxWidth: FORM_MAX_WIDTH,
    width: "100%",
    // The helper text carries its own top padding, so this is the gap between
    // the heading, the two fields and the button.
    gap: spacing.sm,
  },
});
